import { Link } from 'react-router-dom';
import { ROUTES } from '../../lib/constants';
import { SectionShell } from './SectionShell';
import { SectionHeader } from './SectionHeader';
import { CARD_ENTRANCE, staggerStyle } from '../../lib/animation';
import { V2_PLACEHOLDER_PROPS } from '../../lib/v2-deferred';

interface ViewRow {
  readonly idx: '01' | '02' | '03' | '04' | '05';
  readonly name: string;
  readonly desc: string;
  readonly ctaLabel: string;
  readonly route: string | null;
  readonly iconBg: string;
  readonly iconStroke: string;
  readonly icon: React.ReactNode;
}

// 2026-07-02 homepage copy refresh (Georgetown / Frank spec PDF):
// each view description rewritten to Georgetown's preferred wording.
// View names also aligned to the spec ("Map" / "Rankings" / "Detail" /
// "Compare" / "Providers"). Icons + colors + CTAs unchanged.
const VIEWS: ReadonlyArray<ViewRow> = [
  {
    idx: '01',
    name: 'Map',
    desc:
      'See annual job openings and credentials-to-jobs alignment in middle-skills springboard occupations. A continuous red-to-green color scale shows shortage severity at a glance, filterable by any of the 22 occupational groups.',
    ctaLabel: 'Open Map →',
    route: ROUTES.map,
    iconBg: 'bg-gold/10',
    iconStroke: 'stroke-gold-dark',
    icon: (
      <>
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
        <circle cx="12" cy="9" r="2.5" />
      </>
    ),
  },
  {
    idx: '02',
    name: 'Rankings',
    desc:
      'Sort and compare all 55 metro areas by credentials-to-jobs ratio, annual credential production, or projected demand. Find which cities lead or lag in any of the 22 occupational groups containing springboard occupations.',
    ctaLabel: 'Open Rankings →',
    route: ROUTES.rankings,
    iconBg: 'bg-blue/10',
    iconStroke: 'stroke-blue',
    icon: (
      <>
        <rect x="2" y="10" width="5" height="12" rx="1" />
        <rect x="9.5" y="6" width="5" height="16" rx="1" />
        <rect x="17" y="2" width="5" height="20" rx="1" />
      </>
    ),
  },
  {
    idx: '03',
    name: 'Detail',
    desc:
      'A dashboard for diving deep into individual metro areas – showing a list of springboard occupations, the credentials awarded and job openings annually, and alignment by occupational group.',
    ctaLabel: 'Open Detail →',
    route: ROUTES.detail,
    // Prototype line 1180: bg=var(--red-bg) (#fceef0 = our `rose-bg` token).
    iconBg: 'bg-rose-bg',
    iconStroke: 'stroke-rose',
    icon: (
      <>
        <circle cx="11" cy="11" r="7" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
        <line x1="11" y1="8" x2="11" y2="14" />
        <line x1="8" y1="11" x2="14" y2="11" />
      </>
    ),
  },
  {
    idx: '04',
    name: 'Compare',
    desc:
      'Select two or three metro areas for a side-by-side comparison of springboard alignment overall and by occupational group.',
    ctaLabel: 'Open Compare →',
    route: ROUTES.compare,
    // Prototype line 1200: bg=var(--green-bg) + stroke=var(--green2).
    iconBg: 'bg-green2/[0.10]',
    iconStroke: 'stroke-green2',
    icon: (
      <>
        <rect x="2" y="3" width="8" height="18" rx="1" />
        <rect x="14" y="3" width="8" height="18" rx="1" />
      </>
    ),
  },
  {
    idx: '05',
    name: 'Providers',
    desc:
      'Browse individual credential providers within any metro area. See which programs each provider offers, their annual production of aligned credentials, and their potential contribution to addressing credential shortages.',
    ctaLabel: 'Open Providers →',
    route: ROUTES.providers,
    // Prototype lines 1218-1219: bg=var(--teal-bg) (#e0f2fe) + stroke=var(--teal)
    // (#0891B2 in home.html's token block, = our `teal-dark` token).
    iconBg: 'bg-teal-dark/[0.10]',
    iconStroke: 'stroke-teal-dark',
    icon: (
      <>
        <rect x="2" y="7" width="20" height="14" rx="2" />
        <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
        <line x1="12" y1="12" x2="12" y2="16" />
        <line x1="10" y1="14" x2="14" y2="14" />
      </>
    ),
  },
];

function ViewIcon({ view }: { readonly view: ViewRow }) {
  return (
    <span
      aria-hidden="true"
      className={`flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-lg border border-border ${view.iconBg}`}
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={view.iconStroke}
      >
        {view.icon}
      </svg>
    </span>
  );
}

/**
 * "03 · Data Views" — five rows summarizing each view in the tool.
 * V1 ships only Map (01) and Rankings (02); the other three show a
 * "Coming soon" pill instead of a CTA link, matching the deferred V2 scope.
 */
export function DataViewsSection() {
  return (
    <SectionShell id="data-views">
      <SectionHeader
        eyebrow="02 · Data Views"
        headline="Five ways to explore the data"
        lead="See the big picture through the Map and Rankings views, explore the specifics through the Details view, and view metro areas side-by-side through the Compare view. Finally, explore credential production across all 55 metro areas through the Provider view."
      />

      <div className="space-y-3">
          {VIEWS.map((view, idx) => (
            <div
              key={view.idx}
              // Row hover lift + bigger shadow per `_archive/home.html` lines
              // 391-394: `transform: translateY(-1px)` + a beefier panel shadow.
              // Entrance animation (CARD_ENTRANCE + staggerStyle) shared with
              // step cards + persona cards via src/lib/animation.ts.
              className={`flex flex-col items-start gap-4 rounded-lg border border-border bg-white p-6 shadow-card transition hover:-translate-y-px hover:shadow-[0_6px_20px_rgba(20,65,117,0.10),0_2px_6px_rgba(20,65,117,0.06)] md:flex-row md:items-center md:gap-6 ${CARD_ENTRANCE}`}
              style={staggerStyle(idx)}
            >
              <div className="flex flex-1 items-start gap-5">
                <ViewIcon view={view} />
                <div>
                  <p className="mb-1 text-meta font-extrabold uppercase text-text-4">
                    View {view.idx}
                  </p>
                  <h3 className="mb-1.5 text-base font-bold text-text-1">{view.name}</h3>
                  {/* CR-022: body copy raised text-[13px] → text-base (16px). */}
                  <p className="text-base leading-[1.65] text-text-3">{view.desc}</p>
                </div>
              </div>
              <div className="md:shrink-0">
                {view.route ? (
                  <Link
                    to={view.route}
                    // CTA hover state per prototype lines 415-418: bg lifts to
                    // rgba(0,116,204,.14) and gets a soft blue-tinted shadow.
                    className="inline-flex min-w-[140px] items-center justify-center rounded-pill border border-blue/15 bg-ice px-0 py-2 text-xs font-bold text-blue-link transition hover:bg-blue/[0.14] hover:shadow-[0_2px_8px_rgba(0,116,204,0.18)]"
                  >
                    {view.ctaLabel}
                  </Link>
                ) : (
                  <span
                    {...V2_PLACEHOLDER_PROPS}
                    className="inline-flex min-w-[140px] cursor-not-allowed items-center justify-center rounded-pill border border-border bg-surface px-0 py-2 text-xs font-bold text-text-4"
                  >
                    {view.ctaLabel}
                  </span>
                )}
              </div>
            </div>
          ))}
      </div>
    </SectionShell>
  );
}
